import { useEffect, useId, useState } from 'react';
import { toast } from 'react-toastify';
import API from '../../../Utils/api';
import { formatShortDate } from '../../../Utils/dates/formatDate';
import { AdminSurface, Button, EmptyState, ErrorState, Field, LoadingState, PageHeader, Panel, StatusPill } from '../../../Components/AdminSurface';
import styles from './FeedbackInspection.module.css';

const STATUSES = ['Open', 'Acknowledged', 'Resolved'];
const TONES = { Open: 'warn', Acknowledged: 'neutral', Resolved: 'good' };

// Legacy records may lack a date; new Date(null) is 1970 and bad strings are NaN, so both are guarded.
const formatDate = value => {
    if (value == null || value === '' || Number.isNaN(new Date(value).getTime())) return 'Date not recorded';
    return `${formatShortDate(value)} ${new Date(value).getFullYear()}`;
};

const Feedback = ({ data: feedback }) => {
    const [saved, setSaved] = useState(feedback.status);
    const [value, setValue] = useState(feedback.status);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const headingId = useId();

    const onUpdate = async () => {
        setBusy(true);
        setMessage('');
        try {
            await API.updateFeedback({ feedbackId: feedback.id, status: value });
            setSaved(value);
            setMessage('Status updated.');
            toast.success('Feedback State Updated', { position: 'bottom-center' });
        } catch (error) {
            setMessage('Failed to update status.');
            toast.error('Failed to Update Feedback Status', { position: 'bottom-center' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <li className={styles.item}>
            <article aria-labelledby={headingId} className={styles.card}>
                <div className={styles.top}>
                    <h3 id={headingId} className={styles.subject}>
                        {feedback.subject}
                    </h3>
                    <StatusPill tone={TONES[saved] || 'neutral'}>{saved}</StatusPill>
                </div>
                <p className={styles.meta}>
                    {feedback.type} · {formatDate(feedback.date)} · {feedback.name} ({feedback.email})
                </p>
                <p className={styles.message}>{feedback.message}</p>
                <div className={styles.actions}>
                    <div className={styles.statusField}>
                        <Field as="select" label="Status" value={value} onChange={e => setValue(e.target.value)}>
                            {STATUSES.map(s => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </Field>
                    </div>
                    <Button variant="primary" busy={busy} disabled={value === saved} onClick={onUpdate}>
                        Update
                    </Button>
                    <span role="status" aria-live="polite" className={styles.note}>
                        {message}
                    </span>
                </div>
            </article>
        </li>
    );
};

const PAGE_SIZE = 10;

export default function FeedbackInspection() {
    const [result, setResult] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [filter, setFilter] = useState('All');
    const [page, setPage] = useState(1);
    const [pending, setPending] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoadError(false);
        setPending(true);
        API.getAdminFeedbackInspection({ page, limit: PAGE_SIZE, status: filter })
            .then(({ data }) => !cancelled && setResult(data))
            .catch(() => !cancelled && setLoadError(true))
            .finally(() => !cancelled && setPending(false));
        return () => {
            cancelled = true;
        };
    }, [page, filter, attempt]);

    const changeFilter = next => {
        setFilter(next);
        setPage(1);
    };

    let body;
    let sub;
    if (loadError) body = <ErrorState label="feedback" onRetry={() => setAttempt(a => a + 1)} />;
    else if (result === null) body = <LoadingState label="feedback" />;
    else {
        const { counts, items, pages, total } = result;
        sub = `${counts.All} ${counts.All === 1 ? 'item' : 'items'} · ${counts.Open} open`;
        body = counts.All === 0 ? (
            <EmptyState>No feedback yet.</EmptyState>
        ) : (
            <>
                <div role="group" aria-label="Filter by status" className={styles.chips}>
                    {['All', ...STATUSES].map(s => (
                        <Button key={s} aria-pressed={filter === s} onClick={() => changeFilter(s)}>
                            {`${s} (${counts[s]})`}
                        </Button>
                    ))}
                </div>
                {total === 0 ? (
                    <EmptyState>No feedback with this status.</EmptyState>
                ) : (
                    <>
                        <ul className={styles.list} aria-busy={pending}>
                            {items.map(fb => (
                                <Feedback key={fb.id} data={fb} />
                            ))}
                        </ul>
                        <nav className={styles.pager} aria-label="Feedback pages">
                            <Button disabled={pending || page <= 1} onClick={() => setPage(p => p - 1)}>
                                Previous
                            </Button>
                            <span>{`Page ${result.page} of ${pages}`}</span>
                            <Button disabled={pending || page >= pages} onClick={() => setPage(p => p + 1)}>
                                Next
                            </Button>
                        </nav>
                    </>
                )}
            </>
        );
    }

    return (
        <AdminSurface>
            <PageHeader title="Feedback" sub={sub} />
            <Panel title="Submissions">{body}</Panel>
        </AdminSurface>
    );
}
